import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { MoreHorizontal, Pause, Play, Search, Wallet } from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

import { PeriodControl } from '../../../components/design-system/analytics/PeriodControl';
import { PageContainer } from '../../../common/components/PageContainer';
import { AdCampaignReport, AdsRange, useAdsApi } from '../../../common/hooks/use-ads-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import {
    CampaignStatusBadge, ConnectFirst, PageHeading, ProviderMark, formatMoney, formatNumber, formatPct, formatRoas,
    readApiError, useAdConnections, useAdsRange,
} from './ads-shared';

type ProviderFilter = 'all' | 'meta' | 'google';
type StatusFilter = 'all' | 'active' | 'paused';

const CampaignsBody = () => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const connections = useAdConnections();
    const { range, periodProps } = useAdsRange();
    const [campaigns, setCampaigns] = useState<AdCampaignReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [provider, setProvider] = useState<ProviderFilter>('all');
    const [status, setStatus] = useState<StatusFilter>('all');
    const [reloadKey, setReloadKey] = useState(0);

    const [open, setOpen] = useState<AdCampaignReport | null>(null);
    const [statusTarget, setStatusTarget] = useState<AdCampaignReport | null>(null);
    const [budgetTarget, setBudgetTarget] = useState<AdCampaignReport | null>(null);

    useEffect(() => {
        if (!connections.hasConnection) return;
        let cancelled = false;
        setLoading(true);
        api.getCampaigns(range)
            .then((r) => { if (!cancelled) setCampaigns(r.campaigns); })
            .catch((error) => { console.error('Error loading campaigns:', error); if (!cancelled) setCampaigns([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate, connections.hasConnection, reloadKey]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return campaigns.filter((c) =>
            (provider === 'all' || c.provider === provider) &&
            (status === 'all' || c.status === status) &&
            (!term || c.name.toLowerCase().includes(term)));
    }, [campaigns, search, provider, status]);

    const heading = (
        <PageHeading
            title={t('ADS.CAMPAIGNS.TITLE', 'Campaigns')}
            subtitle={t('ADS.CAMPAIGNS.SUBTITLE', 'Every campaign in your connected accounts, with what the platform reports beside what your site recorded.')}
        >
            {connections.hasConnection && <PeriodControl {...periodProps} />}
        </PageHeading>
    );

    if (connections.loading) return <div className="flex flex-col gap-6">{heading}<Skeleton className="h-64 w-full" /></div>;
    if (!connections.hasConnection) return <div className="flex flex-col gap-6">{heading}<ConnectFirst /></div>;

    return (
        <div className="flex flex-col gap-6">
            {heading}

            <div className="flex flex-wrap items-center gap-3">
                <InputGroup className="w-full sm:w-72">
                    <InputGroupAddon><Search /></InputGroupAddon>
                    <InputGroupInput
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('ADS.CAMPAIGNS.SEARCH', 'Search campaigns')}
                    />
                </InputGroup>
                <ToggleGroup type="single" variant="outline" size="sm" value={provider} onValueChange={(v) => v && setProvider(v as ProviderFilter)}>
                    <ToggleGroupItem value="all">{t('ADS.COMMON.ALL', 'All')}</ToggleGroupItem>
                    <ToggleGroupItem value="meta">Meta</ToggleGroupItem>
                    <ToggleGroupItem value="google">Google</ToggleGroupItem>
                </ToggleGroup>
                <ToggleGroup type="single" variant="outline" size="sm" value={status} onValueChange={(v) => v && setStatus(v as StatusFilter)}>
                    <ToggleGroupItem value="all">{t('ADS.COMMON.ALL', 'All')}</ToggleGroupItem>
                    <ToggleGroupItem value="active">{t('ADS.STATUS.ACTIVE', 'Active')}</ToggleGroupItem>
                    <ToggleGroupItem value="paused">{t('ADS.STATUS.PAUSED', 'Paused')}</ToggleGroupItem>
                </ToggleGroup>
            </div>

            <Card className="py-0">
                <CardContent className="px-0">
                    {loading ? <Skeleton className="m-4 h-64" /> : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="ps-4">{t('ADS.CAMPAIGNS.NAME', 'Campaign')}</TableHead>
                                    <TableHead>{t('ADS.CAMPAIGNS.STATUS', 'Status')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.CAMPAIGNS.DAILY_BUDGET', 'Daily budget')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.SPEND', 'Spend')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.CLICKS', 'Clicks')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.ENGAGED', 'Engaged visitors')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.LEADS', 'Enquiries')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.COST_PER_LEAD', 'Cost per enquiry')}</TableHead>
                                    <TableHead className="text-end">{t('ADS.METRIC.PLATFORM_CONVERSIONS', 'Platform conversions')}</TableHead>
                                    <TableHead className="w-10 pe-4"><span className="sr-only">{t('ADS.COMMON.ACTIONS', 'Actions')}</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {visible.map((c) => (
                                    <TableRow key={`${c.provider}:${c.externalId}`} className="cursor-pointer" onClick={() => setOpen(c)}>
                                        <TableCell className="ps-4">
                                            <span className="flex min-w-0 max-w-80 items-center gap-2">
                                                <ProviderMark provider={c.provider} />
                                                <span className="truncate" title={c.name}>{c.name}</span>
                                            </span>
                                        </TableCell>
                                        <TableCell><CampaignStatusBadge status={c.status} /></TableCell>
                                        <TableCell className="text-end tabular-nums">{formatMoney(c.dailyBudget, c.currency, 2)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatMoney(c.spend, c.currency)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(c.clicks)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(c.ours.engagements)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatNumber(c.ours.leads)}</TableCell>
                                        <TableCell className="text-end tabular-nums">{formatMoney(c.costPerLead, c.currency, 2)}</TableCell>
                                        <TableCell className="text-end tabular-nums text-muted-foreground">{formatNumber(c.providerConversions)}</TableCell>
                                        <TableCell className="pe-4" onClick={(e) => e.stopPropagation()}>
                                            {c.status !== 'ended' && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon-sm" aria-label={t('ADS.COMMON.ACTIONS', 'Actions')}>
                                                            <MoreHorizontal />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuGroup>
                                                            <DropdownMenuItem onSelect={() => setStatusTarget(c)}>
                                                                {c.status === 'active' ? <Pause /> : <Play />}
                                                                {c.status === 'active' ? t('ADS.CAMPAIGNS.PAUSE', 'Pause') : t('ADS.CAMPAIGNS.RESUME', 'Resume')}
                                                            </DropdownMenuItem>
                                                            {c.dailyBudget !== undefined && (
                                                                <DropdownMenuItem onSelect={() => setBudgetTarget(c)}>
                                                                    <Wallet />
                                                                    {t('ADS.CAMPAIGNS.CHANGE_BUDGET', 'Change daily budget')}
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuGroup>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!visible.length && (
                                    <TableRow>
                                        <TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">
                                            {campaigns.length
                                                ? t('ADS.CAMPAIGNS.NO_MATCH', 'No campaign matches these filters.')
                                                : t('ADS.CAMPAIGNS.EMPTY', 'No campaigns yet. Press Sync now to pull them from your accounts.')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <CampaignSheet campaign={open} range={range} onClose={() => setOpen(null)} />

            <StatusDialog
                campaign={statusTarget}
                onClose={() => setStatusTarget(null)}
                onDone={() => { setStatusTarget(null); setReloadKey((k) => k + 1); }}
            />
            <BudgetDialog
                campaign={budgetTarget}
                onClose={() => setBudgetTarget(null)}
                onDone={() => { setBudgetTarget(null); setReloadKey((k) => k + 1); }}
            />
        </div>
    );
};

/** One campaign: its day-by-day line and both halves of its results. */
const CampaignSheet = ({ campaign, range, onClose }: { campaign: AdCampaignReport | null; range: AdsRange; onClose: () => void }) => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const [series, setSeries] = useState<Array<{ date: string; spend: number; clicks: number }>>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!campaign) return;
        let cancelled = false;
        setLoading(true);
        api.getCampaign(campaign.provider, campaign.externalId, range)
            .then((r) => { if (!cancelled) setSeries(r.series); })
            .catch(() => { if (!cancelled) setSeries([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign?.provider, campaign?.externalId, range.startDate, range.endDate]);

    const chartConfig: ChartConfig = { spend: { label: t('ADS.METRIC.SPEND', 'Spend'), color: 'var(--foreground)' } };
    const row = (label: string, value: string) => (
        <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );

    return (
        <Sheet open={!!campaign} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
                {campaign && (
                    <>
                        <SheetHeader>
                            <SheetTitle className="flex items-center gap-2"><ProviderMark provider={campaign.provider} />{campaign.name}</SheetTitle>
                            <SheetDescription className="flex items-center gap-3">
                                <CampaignStatusBadge status={campaign.status} />
                                {campaign.utmCampaign && <span>utm_campaign: {campaign.utmCampaign}</span>}
                            </SheetDescription>
                        </SheetHeader>
                        <div className="flex flex-col gap-6 px-4 pb-6">
                            {loading ? <Skeleton className="h-48 w-full" /> : (
                                <ChartContainer config={chartConfig} className="h-48 w-full">
                                    <AreaChart data={series} margin={{ left: 0, right: 8, top: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(d: string) => d.slice(5)} />
                                        <YAxis tickLine={false} axisLine={false} width={40} />
                                        <ChartTooltip content={<ChartTooltipContent />} />
                                        <Area dataKey="spend" type="monotone" stroke="var(--color-spend)" strokeWidth={2} fill="var(--color-spend)" fillOpacity={0.08} />
                                    </AreaChart>
                                </ChartContainer>
                            )}

                            <section>
                                <h3 className="mb-1 text-sm font-medium">{t('ADS.DETAIL.ON_YOUR_SITE', 'On your site')}</h3>
                                <p className="mb-2 text-xs text-muted-foreground">{t('ADS.DETAIL.ON_YOUR_SITE_DESC', 'Recorded by us, from visitors tagged with this campaign.')}</p>
                                {row(t('ADS.DETAIL.VISITORS', 'Visitors'), formatNumber(campaign.ours.visitors))}
                                {row(t('ADS.METRIC.ENGAGED', 'Engaged visitors'), formatNumber(campaign.ours.engagements))}
                                {row(t('ADS.METRIC.ROOM_UPLOADS', 'Rooms uploaded'), formatNumber(campaign.ours.roomUploads))}
                                {row(t('ADS.METRIC.CHATS', 'Assistant chats'), formatNumber(campaign.ours.chats))}
                                {row(t('ADS.DETAIL.CART_CLICKS', 'Add to cart'), formatNumber(campaign.ours.cartClicks))}
                                {row(t('ADS.METRIC.LEADS', 'Enquiries'), formatNumber(campaign.ours.leads))}
                                {row(t('ADS.METRIC.COST_PER_LEAD', 'Cost per enquiry'), formatMoney(campaign.costPerLead, campaign.currency, 2))}
                            </section>

                            <Separator />

                            <section>
                                <h3 className="mb-1 text-sm font-medium">{t('ADS.DETAIL.PLATFORM_SAYS', 'What {{platform}} reports', { platform: campaign.provider === 'google' ? 'Google' : 'Meta' })}</h3>
                                <p className="mb-2 text-xs text-muted-foreground">{t('ADS.DETAIL.PLATFORM_SAYS_DESC', 'The platform’s own counting, which claims every conversion it can.')}</p>
                                {row(t('ADS.METRIC.SPEND', 'Spend'), formatMoney(campaign.spend, campaign.currency, 2))}
                                {row(t('ADS.DETAIL.IMPRESSIONS', 'Impressions'), formatNumber(campaign.impressions))}
                                {row(t('ADS.METRIC.CLICKS', 'Clicks'), formatNumber(campaign.clicks))}
                                {row(t('ADS.DETAIL.CTR', 'Click rate'), formatPct(campaign.ctr))}
                                {row(t('ADS.DETAIL.CPC', 'Cost per click'), formatMoney(campaign.cpc, campaign.currency, 2))}
                                {row(t('ADS.METRIC.PLATFORM_CONVERSIONS', 'Platform conversions'), formatNumber(campaign.providerConversions))}
                                {row(t('ADS.METRIC.ROAS', 'Return claimed by platforms'), formatRoas(campaign.providerRoas))}
                            </section>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};

const StatusDialog = ({ campaign, onClose, onDone }: { campaign: AdCampaignReport | null; onClose: () => void; onDone: () => void }) => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const [busy, setBusy] = useState(false);
    const pausing = campaign?.status === 'active';

    const confirm = async () => {
        if (!campaign) return;
        setBusy(true);
        try {
            await api.setCampaignStatus(campaign.provider, campaign.externalId, pausing ? 'paused' : 'active');
            toast.success(pausing
                ? t('ADS.CAMPAIGNS.PAUSED_TOAST', '“{{name}}” is paused.', { name: campaign.name })
                : t('ADS.CAMPAIGNS.RESUMED_TOAST', '“{{name}}” is running again.', { name: campaign.name }));
            onDone();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.CAMPAIGNS.CHANGE_FAILED', 'The ad account did not accept the change.')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={!!campaign} onOpenChange={(o) => !o && !busy && onClose()}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {pausing
                            ? t('ADS.CAMPAIGNS.PAUSE_TITLE', 'Pause “{{name}}”?', { name: campaign?.name })
                            : t('ADS.CAMPAIGNS.RESUME_TITLE', 'Resume “{{name}}”?', { name: campaign?.name })}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {pausing
                            ? t('ADS.CAMPAIGNS.PAUSE_BODY', 'It stops spending straight away in your ad account. Its settings are kept, so you can resume it.')
                            : t('ADS.CAMPAIGNS.RESUME_BODY', 'It starts spending again at {{budget}} a day.', {
                                budget: formatMoney(campaign?.dailyBudget, campaign?.currency, 2),
                            })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={busy}>{t('ADS.COMMON.CANCEL', 'Cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy}>
                        {busy && <Spinner data-icon="inline-start" />}
                        {pausing ? t('ADS.CAMPAIGNS.PAUSE', 'Pause') : t('ADS.CAMPAIGNS.RESUME', 'Resume')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

const BudgetDialog = ({ campaign, onClose, onDone }: { campaign: AdCampaignReport | null; onClose: () => void; onDone: () => void }) => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setValue(campaign?.dailyBudget ? String(campaign.dailyBudget) : '');
    }, [campaign]);

    const amount = Number(value);
    const valid = Number.isFinite(amount) && amount > 0;
    const monthlyChange = campaign?.dailyBudget && valid ? (amount - campaign.dailyBudget) * 30 : null;

    const save = async () => {
        if (!campaign || !valid) return;
        setBusy(true);
        try {
            await api.setCampaignBudget(campaign.provider, campaign.externalId, amount);
            toast.success(t('ADS.CAMPAIGNS.BUDGET_TOAST', '“{{name}}” now spends up to {{amount}} a day.', {
                name: campaign.name, amount: formatMoney(amount, campaign.currency, 2),
            }));
            onDone();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.CAMPAIGNS.CHANGE_FAILED', 'The ad account did not accept the change.')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!campaign} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('ADS.CAMPAIGNS.BUDGET_TITLE', 'Daily budget for “{{name}}”', { name: campaign?.name })}</DialogTitle>
                    <DialogDescription>{t('ADS.CAMPAIGNS.BUDGET_BODY', 'Changed in your ad account as soon as you save.')}</DialogDescription>
                </DialogHeader>
                <FieldGroup>
                    <Field>
                        <FieldLabel htmlFor="daily-budget">{t('ADS.CAMPAIGNS.DAILY_BUDGET', 'Daily budget')} {campaign?.currency ? `(${campaign.currency})` : ''}</FieldLabel>
                        <Input id="daily-budget" type="number" min="1" step="1" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
                        {monthlyChange !== null && monthlyChange !== 0 && (
                            <FieldDescription>
                                {monthlyChange > 0
                                    ? t('ADS.CAMPAIGNS.BUDGET_MORE', 'About {{amount}} more a month.', { amount: formatMoney(monthlyChange, campaign?.currency) })
                                    : t('ADS.CAMPAIGNS.BUDGET_LESS', 'About {{amount}} less a month.', { amount: formatMoney(-monthlyChange, campaign?.currency) })}
                            </FieldDescription>
                        )}
                    </Field>
                </FieldGroup>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>{t('ADS.COMMON.CANCEL', 'Cancel')}</Button>
                    <Button onClick={save} disabled={!valid || busy}>
                        {busy && <Spinner data-icon="inline-start" />}
                        {t('ADS.COMMON.SAVE', 'Save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const AdsCampaignsPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ADS.TITLE', 'Ads') }, { label: t('SIDEBAR.ADS.CAMPAIGNS', 'Campaigns') }]}>
            <FeatureGate feature="adsManager">
                <CampaignsBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AdsCampaignsPage;
